(() => {
  if (window.flashDealsLoaded) return;
  window.flashDealsLoaded = true;

  const controllers = new WeakMap();

  const numericValue = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const initializeSection = (section) => {
    if (controllers.has(section)) return controllers.get(section);

    const grid = section.querySelector('[data-flash-deals-grid]');
    if (!grid) return null;

    const pageSize = numericValue(section.dataset.pageSize) || 4;
    const sort = section.querySelector('[data-flash-deals-sort]');
    const hideClaimed = section.querySelector('[data-flash-deals-hide-claimed]');
    const moreWrap = section.querySelector('[data-flash-deals-more-wrap]');
    const moreButton = section.querySelector('[data-flash-deals-more]');
    const empty = section.querySelector('[data-flash-deals-empty]');
    const carouselEnabled = section.dataset.layout === 'carousel';
    const carouselAutoplay = section.dataset.carouselAutoplay === 'true';
    const carouselInterval = numericValue(section.dataset.carouselInterval) || 5000;
    const carouselControls = section.querySelector('[data-flash-deals-carousel-controls]');
    const previousButton = section.querySelector('[data-flash-deals-previous]');
    const nextButton = section.querySelector('[data-flash-deals-next]');
    let visibleLimit = pageSize;
    let autoplayTimer = null;
    let normalizeTimer = null;
    let carouselStep = 0;
    let carouselLoopReady = false;

    const itemScrollLeft = (item) => item.getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft;

    const scrollInstantly = (left) => {
      const previousBehavior = grid.style.scrollBehavior;
      grid.style.scrollBehavior = 'auto';
      grid.scrollLeft = left;
      grid.style.scrollBehavior = previousBehavior;
    };

    const buildCarouselLoop = (eligibleItems) => {
      grid.querySelectorAll('[data-flash-deal-clone]').forEach((clone) => clone.remove());
      carouselStep = 0;
      carouselLoopReady = false;

      if (!carouselEnabled || eligibleItems.length < 2 || grid.scrollWidth <= grid.clientWidth + 1) return;

      const lastItem = eligibleItems[eligibleItems.length - 1];
      grid.prepend(lastItem);
      carouselStep = itemScrollLeft(eligibleItems[0]) - itemScrollLeft(lastItem);
      if (carouselStep <= 0) return;

      scrollInstantly(carouselStep);
      carouselLoopReady = true;
    };

    const normalizeCarouselLoop = () => {
      if (!carouselLoopReady || !carouselStep) return;

      let visibleItems = [...grid.querySelectorAll('[data-flash-deal-item]:not([hidden])')];
      while (grid.scrollLeft < carouselStep * 0.5) {
        const previousScrollLeft = grid.scrollLeft;
        grid.prepend(visibleItems[visibleItems.length - 1]);
        scrollInstantly(previousScrollLeft + carouselStep);
        visibleItems = [...grid.querySelectorAll('[data-flash-deal-item]:not([hidden])')];
      }

      while (grid.scrollLeft > carouselStep * 1.5) {
        const previousScrollLeft = grid.scrollLeft;
        grid.append(visibleItems[0]);
        scrollInstantly(previousScrollLeft - carouselStep);
        visibleItems = [...grid.querySelectorAll('[data-flash-deal-item]:not([hidden])')];
      }
    };

    const compareItems = (first, second) => {
      const firstComingSoon = first.dataset.flashDealKind === 'coming-soon';
      const secondComingSoon = second.dataset.flashDealKind === 'coming-soon';
      if (firstComingSoon !== secondComingSoon) return firstComingSoon ? -1 : 1;

      const firstClaimed = first.dataset.flashDealStatus === 'claimed';
      const secondClaimed = second.dataset.flashDealStatus === 'claimed';
      if (firstClaimed !== secondClaimed) return firstClaimed ? 1 : -1;

      switch (sort?.value) {
        case 'newest': {
          const dateDifference = numericValue(second.dataset.flashDealDate) - numericValue(first.dataset.flashDealDate);
          return dateDifference || numericValue(first.dataset.flashDealPosition) - numericValue(second.dataset.flashDealPosition);
        }
        case 'price-ascending':
          return numericValue(first.dataset.flashDealPrice) - numericValue(second.dataset.flashDealPrice);
        case 'price-descending':
          return numericValue(second.dataset.flashDealPrice) - numericValue(first.dataset.flashDealPrice);
        case 'title-ascending':
          return (first.dataset.flashDealTitle || '').localeCompare(second.dataset.flashDealTitle || '', undefined, { sensitivity: 'base' });
        default:
          return numericValue(first.dataset.flashDealPosition) - numericValue(second.dataset.flashDealPosition);
      }
    };

    const refresh = () => {
      grid.querySelectorAll('[data-flash-deal-clone]').forEach((clone) => clone.remove());
      const items = [...grid.querySelectorAll('[data-flash-deal-item]')];
      items.sort(compareItems).forEach((item) => grid.append(item));

      const eligibleItems = items.filter((item) => {
        if (item.dataset.flashDealExpired === 'true') return false;
        return !(hideClaimed?.checked && item.dataset.flashDealStatus === 'claimed');
      });

      items.forEach((item) => {
        const eligibleIndex = eligibleItems.indexOf(item);
        item.hidden = eligibleIndex === -1 || (!carouselEnabled && eligibleIndex >= visibleLimit);
      });

      const remaining = carouselEnabled ? 0 : Math.max(eligibleItems.length - visibleLimit, 0);
      if (moreWrap) moreWrap.hidden = carouselEnabled || remaining === 0;
      if (moreButton) moreButton.textContent = remaining > 0 ? `Show more (${remaining})` : 'Show more';
      if (empty) empty.hidden = eligibleItems.length !== 0;
      buildCarouselLoop(eligibleItems);
      const carouselCanMove = carouselLoopReady && eligibleItems.length > 1;
      if (carouselControls) carouselControls.hidden = !carouselCanMove;
      if (previousButton) previousButton.disabled = !carouselCanMove;
      if (nextButton) nextButton.disabled = !carouselCanMove;
    };

    const moveCarousel = (direction) => {
      if (!carouselLoopReady || !carouselStep) return;
      normalizeCarouselLoop();
      grid.scrollBy({ left: direction * carouselStep, behavior: 'smooth' });
    };

    if (carouselEnabled) {
      grid.addEventListener('scroll', () => {
        window.clearTimeout(normalizeTimer);
        normalizeTimer = window.setTimeout(normalizeCarouselLoop, 100);
      }, { passive: true });
    }

    if (carouselEnabled && carouselAutoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      autoplayTimer = window.setInterval(() => {
        if (document.hidden || section.matches(':hover') || section.contains(document.activeElement)) return;
        moveCarousel(1);
      }, carouselInterval);
    }

    previousButton?.addEventListener('click', () => moveCarousel(-1));
    nextButton?.addEventListener('click', () => moveCarousel(1));

    sort?.addEventListener('change', () => {
      visibleLimit = pageSize;
      refresh();
    });

    hideClaimed?.addEventListener('change', () => {
      visibleLimit = pageSize;
      refresh();
    });

    moreButton?.addEventListener('click', () => {
      visibleLimit += pageSize;
      refresh();
    });

    const controller = {
      refresh,
      destroy: () => {
        window.clearInterval(autoplayTimer);
        window.clearTimeout(normalizeTimer);
      }
    };
    controllers.set(section, controller);
    refresh();
    return controller;
  };

  const updateCountdown = (item) => {
    const value = item.querySelector('[data-flash-deal-countdown-value]');
    const launchAt = Date.parse(item.dataset.flashDealLaunchAt || '');

    if (!Number.isFinite(launchAt)) {
      if (value) value.textContent = 'Launch time needed';
      return;
    }

    const remainingSeconds = Math.max(Math.ceil((launchAt - Date.now()) / 1000), 0);
    if (remainingSeconds === 0) {
      item.dataset.flashDealExpired = 'true';
      const section = item.closest('[data-flash-deals-section]');
      controllers.get(section)?.refresh();

      const reloadKey = `flash-deal-reveal-${item.dataset.flashDealLaunchAt}`;
      let shouldReload = true;
      try {
        shouldReload = window.sessionStorage.getItem(reloadKey) !== 'attempted';
        window.sessionStorage.setItem(reloadKey, 'attempted');
      } catch (_error) {
        // A blocked sessionStorage should not prevent the scheduled reveal.
      }

      if (shouldReload) {
        if (value) value.textContent = 'Revealing deal…';
        window.setTimeout(() => window.location.reload(), 250);
      }
      return;
    }

    const days = Math.floor(remainingSeconds / 86400);
    const hours = Math.floor((remainingSeconds % 86400) / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;
    const dayText = days > 0 ? `${days}d ` : '';
    if (value) value.textContent = `${dayText}${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  };

  const updateClaimedArchive = (item) => {
    const archiveAt = Number.parseInt(item.dataset.flashDealArchiveAt || '', 10);
    if (!Number.isFinite(archiveAt) || Date.now() < archiveAt * 1000) return;

    item.dataset.flashDealExpired = 'true';
    const section = item.closest('[data-flash-deals-section]');
    controllers.get(section)?.refresh();
  };

  let activeSizeSelection = null;

  const clearDealSelection = () => {
    if (!activeSizeSelection) return;
    window.clearTimeout(activeSizeSelection.openTimer);
    activeSizeSelection.section?.classList.remove('flash-deals--deal-selected');
    activeSizeSelection.card?.classList.remove('flash-deal-card-wrapper--flashing');
    activeSizeSelection = null;
  };

  const normalizeVariantId = (variantId) => {
    const match = String(variantId || '').trim().match(/(?:^|\/)(\d+)$/);
    return match ? match[1] : '';
  };

  const replaceCartVariant = (checkoutUrl, anchorVariantValue, selectedVariantValue) => {
    const anchorVariantId = normalizeVariantId(anchorVariantValue);
    const selectedVariantId = normalizeVariantId(selectedVariantValue);
    if (!anchorVariantId || !selectedVariantId) {
      throw new Error('The size variants are not configured correctly.');
    }

    const url = new URL(checkoutUrl, window.location.origin);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') {
      throw new Error('The checkout URL is not valid.');
    }

    const pathSegments = url.pathname.split('/');
    const cartIndex = pathSegments.indexOf('cart');
    const cartLinesIndex = cartIndex + 1;
    if (cartIndex === -1 || !pathSegments[cartLinesIndex]) {
      throw new Error('This deal needs a standard Shopify cart permalink.');
    }

    const cartLines = decodeURIComponent(pathSegments[cartLinesIndex]).split(',');
    let anchorFound = false;
    const updatedLines = cartLines.map((line) => {
      const match = line.match(/^(\d+):(\d+)$/);
      if (!match || match[1] !== anchorVariantId) return line;
      anchorFound = true;
      return `${selectedVariantId}:${match[2]}`;
    });

    if (!anchorFound) {
      throw new Error('The deal checkout URL does not contain its representative variant.');
    }

    pathSegments[cartLinesIndex] = updatedLines.join(',');
    url.pathname = pathSegments.join('/');
    return url.href;
  };

  const openSizeDialog = (link, card) => {
    const section = link.closest('[data-flash-deals-section]');
    const dialog = section?.querySelector('[data-flash-deal-size-dialog]');
    const optionsTemplate = card.querySelector('[data-flash-deal-size-options]');
    const optionsTarget = dialog?.querySelector('[data-flash-deal-size-options-target]');
    if (!section || !dialog || !optionsTemplate || !optionsTarget) return false;

    clearDealSelection();
    optionsTarget.replaceChildren(optionsTemplate.content.cloneNode(true));

    const dealTitle = link.closest('[data-flash-deal-item]')?.dataset.flashDealTitle || 'this deal';
    const description = dialog.querySelector('[data-flash-deal-size-description]');
    const error = dialog.querySelector('[data-flash-deal-size-error]');
    const preview = dialog.querySelector('[data-flash-deal-size-preview]');
    const previewImage = dialog.querySelector('[data-flash-deal-size-preview-image]');
    const previewLabel = dialog.querySelector('[data-flash-deal-size-preview-label]');
    const continueButton = dialog.querySelector('[data-flash-deal-size-continue]');
    if (description) description.textContent = `Select a size for ${dealTitle} to continue to checkout.`;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
    if (preview) preview.hidden = true;
    if (previewImage) {
      previewImage.hidden = true;
      previewImage.removeAttribute('src');
      previewImage.alt = '';
    }
    if (previewLabel) previewLabel.textContent = '';
    if (continueButton) continueButton.disabled = true;

    section.classList.add('flash-deals--deal-selected');
    card.classList.add('flash-deal-card-wrapper--flashing');
    activeSizeSelection = {
      anchorVariantId: card.dataset.flashDealVariantId || '',
      card,
      checkoutUrl: link.href,
      dialog,
      openTimer: null,
      selectedCheckoutUrl: '',
      section
    };

    dialog.addEventListener('close', () => {
      if (activeSizeSelection?.dialog === dialog) clearDealSelection();
    }, { once: true });

    const openDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 65;
    activeSizeSelection.openTimer = window.setTimeout(() => {
      if (activeSizeSelection?.dialog !== dialog) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      optionsTarget.querySelector('[data-flash-deal-size-choice]:not([disabled])')?.focus();
    }, openDelay);
    return true;
  };

  const initializeAll = (root = document) => {
    if (root instanceof Element && root.matches('[data-flash-deals-section]')) initializeSection(root);
    root.querySelectorAll('[data-flash-deals-section]').forEach(initializeSection);
    if (root instanceof Element && root.matches('[data-flash-deal-launch-at]')) updateCountdown(root);
    root.querySelectorAll('[data-flash-deal-launch-at]').forEach(updateCountdown);
    if (root instanceof Element && root.matches('[data-flash-deal-archive-at]')) updateClaimedArchive(root);
    root.querySelectorAll('[data-flash-deal-archive-at]').forEach(updateClaimedArchive);
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const continueButton = event.target.closest('[data-flash-deal-size-continue]');
    if (continueButton) {
      if (continueButton.disabled || !activeSizeSelection?.selectedCheckoutUrl) return;
      continueButton.disabled = true;
      continueButton.setAttribute('aria-busy', 'true');
      window.location.assign(activeSizeSelection.selectedCheckoutUrl);
      return;
    }

    const sizeChoice = event.target.closest('[data-flash-deal-size-choice]');
    if (sizeChoice) {
      if (sizeChoice.disabled || !activeSizeSelection) return;

      const error = activeSizeSelection.dialog.querySelector('[data-flash-deal-size-error]');
      try {
        const checkoutUrl = replaceCartVariant(
          activeSizeSelection.checkoutUrl,
          activeSizeSelection.anchorVariantId,
          sizeChoice.dataset.flashDealSizeVariantId || ''
        );
        activeSizeSelection.selectedCheckoutUrl = checkoutUrl;
        activeSizeSelection.dialog.querySelectorAll('[data-flash-deal-size-choice]').forEach((choice) => {
          choice.classList.remove('flash-deal-size-dialog__option--selected');
          choice.setAttribute('aria-pressed', 'false');
        });
        sizeChoice.classList.add('flash-deal-size-dialog__option--selected');
        sizeChoice.setAttribute('aria-pressed', 'true');

        const preview = activeSizeSelection.dialog.querySelector('[data-flash-deal-size-preview]');
        const previewImage = activeSizeSelection.dialog.querySelector('[data-flash-deal-size-preview-image]');
        const previewLabel = activeSizeSelection.dialog.querySelector('[data-flash-deal-size-preview-label]');
        const continueButton = activeSizeSelection.dialog.querySelector('[data-flash-deal-size-continue]');
        const selectedLabel = sizeChoice.dataset.flashDealSizeLabel || sizeChoice.textContent.trim();
        const selectedImageUrl = sizeChoice.dataset.flashDealSizeImageUrl || '';
        if (preview) preview.hidden = false;
        if (previewLabel) previewLabel.textContent = selectedLabel;
        if (previewImage) {
          previewImage.hidden = !selectedImageUrl;
          previewImage.alt = selectedImageUrl ? `${selectedLabel} preview` : '';
          if (selectedImageUrl) previewImage.src = selectedImageUrl;
          else previewImage.removeAttribute('src');
        }
        if (continueButton) {
          continueButton.disabled = false;
          continueButton.removeAttribute('aria-busy');
        }
        preview?.scrollIntoView({ block: 'nearest' });
        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      } catch (selectionError) {
        if (error) {
          error.textContent = selectionError instanceof Error ? selectionError.message : 'Unable to prepare this deal. Please try again.';
          error.hidden = false;
        }
      }
      return;
    }

    const link = event.target.closest('.flash-deal-card__link');
    if (!link || event.defaultPrevented || event.button !== 0) return;

    const card = link.closest('.flash-deal-card-wrapper');
    if (!card || card.classList.contains('flash-deal-card-wrapper--flashing')) return;

    if (card.querySelector('[data-flash-deal-size-options]')) {
      event.preventDefault();
      openSizeDialog(link, card);
      return;
    }

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    event.preventDefault();
    link.closest('.flash-deals')?.classList.add('flash-deals--deal-selected');
    card.classList.add('flash-deal-card-wrapper--flashing');

    window.setTimeout(() => {
      window.location.assign(link.href);
    }, 125);
  });

  document.addEventListener('pointermove', (event) => {
    if (!(event.target instanceof Element)) return;

    const section = event.target.closest('.flash-deals');
    if (!section) return;

    const sectionRect = section.getBoundingClientRect();
    section.style.setProperty('--flash-section-x', `${((event.clientX - sectionRect.left) / sectionRect.width) * 100}%`);
    section.style.setProperty('--flash-section-y', `${((event.clientY - sectionRect.top) / sectionRect.height) * 100}%`);

    const card = event.target.closest('.flash-deal-card-wrapper');
    if (!card) return;

    const cardRect = card.getBoundingClientRect();
    card.style.setProperty('--flash-pointer-x', `${((event.clientX - cardRect.left) / cardRect.width) * 100}%`);
    card.style.setProperty('--flash-pointer-y', `${((event.clientY - cardRect.top) / cardRect.height) * 100}%`);
  });

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-flash-deals-section]') ? event.target : event.target.querySelector?.('[data-flash-deals-section]');
    if (!section) return;
    controllers.get(section)?.destroy();
    controllers.delete(section);
  });
  initializeAll();

  window.setInterval(() => {
    document.querySelectorAll('[data-flash-deal-launch-at]:not([data-flash-deal-expired="true"])').forEach(updateCountdown);
    document.querySelectorAll('[data-flash-deal-archive-at]:not([data-flash-deal-expired="true"])').forEach(updateClaimedArchive);
  }, 1000);
})();
